import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, RefreshCw, ExternalLink, Check } from "lucide-react";

/**
 * Admin dialog to add or import activities for a specific runner.
 * Two tabs:
 *   1. STRAVA — fetch the runner's recent Strava activities and pull in any
 *      that are missing from OSCR.
 *   2. MANUAL — log an activity by hand (for runners without Strava or when
 *      Strava is missing the workout).
 * Notifies the parent when totals change so the runners table refreshes.
 */
export default function AdminAddActivityDialog({ runner, children, onChange }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("strava");

  useEffect(() => {
    if (open) setTab(runner?.strava_athlete_id ? "strava" : "manual");
  }, [open, runner]);

  if (!runner) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid="admin-add-activity-dialog"
        className="bg-zinc-950 border-zinc-800 rounded-none text-white max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-wider">
            LOG ACTIVITY FOR · <span className="text-orange-500">{runner.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-zinc-800 -mx-6 px-6 mb-4">
          {[
            { id: "strava", label: "FROM STRAVA", disabled: !runner.strava_athlete_id },
            { id: "manual", label: "MANUAL ENTRY", disabled: false },
          ].map((t) => (
            <button
              key={t.id}
              disabled={t.disabled}
              data-testid={`admin-activity-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`font-display tracking-widest text-xs px-4 py-2 border-b-2 transition ${
                tab === t.id
                  ? "border-orange-500 text-orange-500"
                  : t.disabled
                  ? "border-transparent text-zinc-600 cursor-not-allowed"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
              {t.id === "strava" && t.disabled && " · NOT CONNECTED"}
            </button>
          ))}
        </div>

        {tab === "strava" && (
          <StravaTab runner={runner} onImported={onChange} />
        )}
        {tab === "manual" && (
          <ManualTab
            runner={runner}
            onAdded={() => {
              onChange?.();
              setOpen(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function StravaTab({ runner, onImported }) {
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [fetched, setFetched] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/runners/${runner.user_id}/strava/recent`,
        { params: { days } },
      );
      setActivities(data.activities || []);
      setSelected(new Set());
      setFetched(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch Strava");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  const importSelected = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one activity");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post(
        `/admin/runners/${runner.user_id}/strava/import`,
        { strava_activity_ids: Array.from(selected) },
      );
      const okCount = (data.imported || []).length;
      const skipCount = (data.skipped || []).length;
      const errCount = (data.errors || []).length;
      if (okCount > 0) {
        toast.success(`Imported ${okCount} activit${okCount === 1 ? "y" : "ies"}`);
      }
      if (skipCount > 0) {
        toast.warning(`${skipCount} skipped (before sync cutoff)`);
      }
      if (errCount > 0) {
        toast.error(`${errCount} failed — see console`);
        console.warn("Strava import errors", data.errors);
      }
      onImported?.();
      await load(); // Refresh to update already_imported flags
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const missing = activities.filter((a) => !a.already_imported && !a.before_cutoff);

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">
            LOOK BACK (DAYS · MAX 180)
          </Label>
          <Input
            data-testid="strava-days-input"
            type="number"
            min={1}
            max={180}
            value={days}
            onChange={(e) => setDays(Number(e.target.value) || 30)}
            className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono"
          />
        </div>
        <Button
          data-testid="strava-fetch-btn"
          onClick={load}
          disabled={loading}
          className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider"
        >
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "LOADING…" : "FETCH FROM STRAVA"}
        </Button>
      </div>

      {fetched && (
        <div className="flex items-center justify-between border-t border-zinc-800 pt-3">
          <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
            {activities.length} total · {missing.length} missing from OSCR
          </p>
          <Button
            data-testid="strava-import-btn"
            onClick={importSelected}
            disabled={importing || selected.size === 0}
            size="sm"
            className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider"
          >
            {importing ? "IMPORTING…" : `IMPORT ${selected.size || ""}`}
          </Button>
        </div>
      )}

      <div className="hairline bg-zinc-950 divide-y divide-zinc-800 max-h-[45vh] overflow-y-auto">
        {activities.map((a) => {
          const disabled = a.already_imported || a.before_cutoff;
          const isSelected = selected.has(a.strava_activity_id);
          return (
            <label
              key={a.strava_activity_id}
              data-testid={`strava-activity-${a.strava_activity_id}`}
              className={`flex items-center gap-3 p-3 cursor-pointer transition ${
                disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-zinc-900"
              }`}
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={isSelected}
                onChange={() => !disabled && toggle(a.strava_activity_id)}
                className="w-4 h-4 accent-orange-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-white truncate">{a.name}</span>
                  {a.already_imported && (
                    <span className="font-mono text-[9px] tracking-widest uppercase text-orange-500 border border-orange-600/40 px-1.5 py-0.5">
                      <Check size={9} className="inline" /> IMPORTED
                    </span>
                  )}
                  {a.before_cutoff && (
                    <span className="font-mono text-[9px] tracking-widest uppercase text-zinc-500 border border-zinc-700 px-1.5 py-0.5">
                      BEFORE CUTOFF
                    </span>
                  )}
                </div>
                <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mt-0.5">
                  {a.date} · {a.strava_type} · {a.distance_km} KM
                  {a.duration_min ? ` · ${a.duration_min} min` : ""}
                </p>
              </div>
              <a
                href={a.strava_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-zinc-500 hover:text-orange-500"
                title="Open in Strava"
              >
                <ExternalLink size={13} />
              </a>
            </label>
          );
        })}
        {fetched && activities.length === 0 && (
          <div className="p-6 text-center text-zinc-500 font-mono text-sm">
            No Strava activities found in this window.
          </div>
        )}
        {!fetched && (
          <div className="p-6 text-center text-zinc-500 font-mono text-sm">
            Set a look-back window and click FETCH FROM STRAVA.
          </div>
        )}
      </div>
    </div>
  );
}

function ManualTab({ runner, onAdded }) {
  const [busy, setBusy] = useState(false);
  const today = new Date();
  const todayLocal = new Date(today.getTime() - today.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  const [form, setForm] = useState({
    date: todayLocal,
    activity_type: "run",
    distance_km: "",
    duration_min: "",
    notes: "",
    strava_url: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.distance_km) {
      toast.error("Date and distance are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/runners/${runner.user_id}/activities`, {
        date: form.date,
        activity_type: form.activity_type,
        distance_km: Number(form.distance_km),
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        notes: form.notes,
        strava_url: form.strava_url || null,
      });
      toast.success(`Added ${form.distance_km} km for ${runner.name}`);
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add activity");
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-manual-activity-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">DATE</Label>
          <Input
            data-testid="manual-date"
            type="date"
            value={form.date}
            onChange={(e) => set("date", e.target.value)}
            className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white"
          />
        </div>
        <div>
          <Label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">TYPE</Label>
          <select
            data-testid="manual-type"
            value={form.activity_type}
            onChange={(e) => set("activity_type", e.target.value)}
            className="mt-2 w-full rounded-none bg-zinc-950 border border-zinc-800 text-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-600"
          >
            <option value="run">Run</option>
            <option value="walk">Walk</option>
            <option value="cycle">Cycle</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">DISTANCE (KM)</Label>
          <Input
            data-testid="manual-distance"
            type="number"
            step="0.01"
            min="0"
            value={form.distance_km}
            onChange={(e) => set("distance_km", e.target.value)}
            className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono"
          />
        </div>
        <div>
          <Label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">DURATION (MIN)</Label>
          <Input
            data-testid="manual-duration"
            type="number"
            step="0.1"
            min="0"
            value={form.duration_min}
            onChange={(e) => set("duration_min", e.target.value)}
            className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono"
          />
        </div>
      </div>
      <div>
        <Label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">STRAVA URL (OPTIONAL)</Label>
        <Input
          data-testid="manual-strava-url"
          type="url"
          placeholder="https://www.strava.com/activities/..."
          value={form.strava_url}
          onChange={(e) => set("strava_url", e.target.value)}
          className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono text-xs"
        />
      </div>
      <div>
        <Label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">NOTES</Label>
        <Input
          data-testid="manual-notes"
          type="text"
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white"
        />
      </div>
      <div className="flex justify-end pt-2">
        <Button
          data-testid="manual-submit"
          type="submit"
          disabled={busy}
          className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider"
        >
          <Plus size={14} className="mr-2" /> {busy ? "ADDING…" : "ADD ACTIVITY"}
        </Button>
      </div>
    </form>
  );
}
